/**
 * RelatedArticlesStrip — used on food-item pages to surface articles
 * that mention the item. Renders nothing when no matches exist.
 *
 * Reads from /api/cross-links/food-item/{slug}/articles.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RelatedArticlesStrip({ foodItemSlug, palette }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!foodItemSlug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/cross-links/food-item/${encodeURIComponent(foodItemSlug)}/articles`,
          { params: { limit: 6 } },
        );
        if (!cancelled) setItems(r.data?.items || []);
      } catch { /* silent — related strip is optional */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [foodItemSlug]);

  if (loading || items.length === 0) return null;

  const accent = palette?.accent || "#EA580C";
  const bg     = palette?.bg     || "#FFF8EE";
  const line   = palette?.line   || "#E8DFCF";
  const ink    = palette?.ink    || "#1F1512";
  const inkSoft= palette?.inkSoft|| "#6B4A2E";

  return (
    <section
      className="my-10 rounded-2xl border p-5 sm:p-6"
      style={{ background: bg, borderColor: line }}
      data-testid="related-articles-strip"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4" style={{ color: accent }} />
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>
            Related articles
          </div>
        </div>
        <Link
          to="/articles"
          className="text-[11px] font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1"
          style={{ color: accent }}
          data-testid="related-articles-see-all"
        >
          All articles <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((a) => (
          <Link
            key={a.slug}
            to={`/articles/${a.slug}`}
            className="group flex gap-3 p-2 rounded-lg hover:shadow-md transition-shadow"
            style={{ background: "#fff", border: `1px solid ${line}` }}
            data-testid={`related-article-${a.slug}`}
          >
            {a.feature_image ? (
              <img
                src={a.feature_image}
                alt=""
                className="w-16 h-16 rounded-md object-cover flex-none"
                loading="lazy"
              />
            ) : (
              <div className="w-16 h-16 rounded-md flex-none flex items-center justify-center"
                   style={{ background: bg }}>
                <BookOpen className="w-5 h-5 opacity-40" style={{ color: accent }} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium line-clamp-2 group-hover:underline"
                   style={{ color: ink }}>
                {a.title}
              </div>
              {a.excerpt && (
                <div className="text-[11px] mt-1 line-clamp-2" style={{ color: inkSoft }}>
                  {a.excerpt}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
